import { useState, type FC } from "react";
import { useApolloClient } from "@apollo/client";
import NextLink from "next/link";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTranslation } from "@/shared/hooks";
import { isRequestAlreadyOpen } from "../lib/isRequestAlreadyOpen";
import type {
  CancelAssistantRequestMutationType,
  CancelAssistantRequestMutationVariablesType,
  ConfirmAssistantRequestMutationType,
  ConfirmAssistantRequestMutationVariablesType,
} from "@/types/graphql/site.generated";
import {
  CancelAssistantRequestMutation,
  ConfirmAssistantRequestMutation,
} from "../api/graphql";
import {
  setRequestStatus,
  type AssistantUiRequest,
} from "../model/assistantStore";

const TYPE_KEYS: Record<string, string> = {
  REFUND: "assistant.requestTypeRefund",
  ACCESS_PROBLEM: "assistant.requestTypeAccess",
  PAYMENT_PROBLEM: "assistant.requestTypePayment",
  OTHER: "assistant.requestTypeOther",
};

interface AssistantRequestCardProps {
  request: AssistantUiRequest;
  viewerEmail: string | null;
}

/**
 * Черновик обращения к организатору: зритель сам решает, отправлять ли его.
 * Помощник обращение только готовит (DraftRequest), отправка здесь.
 */
export const AssistantRequestCard: FC<AssistantRequestCardProps> = ({
  request,
  viewerEmail,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const confirm = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await client.mutate<
        ConfirmAssistantRequestMutationType,
        ConfirmAssistantRequestMutationVariablesType
      >({
        mutation: ConfirmAssistantRequestMutation,
        variables: { id: request.id },
      });
      setRequestStatus(request.id, "OPEN");
    } catch (e) {
      if (isRequestAlreadyOpen(e)) setRequestStatus(request.id, "ALREADY_OPEN");
      else setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await client.mutate<
        CancelAssistantRequestMutationType,
        CancelAssistantRequestMutationVariablesType
      >({
        mutation: CancelAssistantRequestMutation,
        variables: { id: request.id },
      });
      setRequestStatus(request.id, "CANCELLED");
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  let statusText: string | null = null;
  if (request.status === "OPEN") {
    statusText = t("assistant.requestSent", { email: viewerEmail ?? "" });
  } else if (request.status === "CANCELLED") {
    statusText = t("assistant.requestCancelled");
  } else if (request.status === "ALREADY_OPEN") {
    statusText = t("assistant.requestAlreadyOpen");
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">
          {t("assistant.requestCardTitle")}
        </Typography>
        <Chip
          size="small"
          label={t(TYPE_KEYS[request.type] ?? "assistant.requestTypeOther")}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {request.message}
      </Typography>
      {request.status === "DRAFT" ? (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            disableElevation
            disabled={busy}
            onClick={() => void confirm()}
          >
            {t("assistant.requestSend")}
          </Button>
          <Button size="small" disabled={busy} onClick={() => void cancel()}>
            {t("assistant.requestCancel")}
          </Button>
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {statusText}
          </Typography>
          {/* Переписку по отправленному обращению зритель ведёт на /requests. */}
          {request.status === "OPEN" && (
            <Link
              component={NextLink}
              href="/requests"
              variant="body2"
              sx={{ display: "inline-block", mt: 0.5 }}
            >
              {t("supportRequestsPage.title")}
            </Link>
          )}
        </>
      )}
      {failed && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {t("assistant.errorNoEmail")}
        </Typography>
      )}
    </Paper>
  );
};
